import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import { terminologyApi, type TermProfile } from '@/api/terminologyApi'
import { applyLocales } from '@/i18n/i18n'
import { exportLocaleCsv, importLocaleCsv } from '@/lib/terminologyCsv'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AddLanguageModal } from '@/components/terminology/AddLanguageModal'
import { EditTermsModal } from '@/components/terminology/EditTermsModal'

type Status = { type: 'ok' | 'err'; messages: string[] } | null

/**
 * Languages card: lists terminology profiles as rows with per-locale actions
 * (edit terms, export/import CSV, delete). Add/edit happen in dialogs; the page itself
 * stays form-free.
 */
export function LocaleManager() {
  const { i18n: i18nInstance } = useTranslation()
  const queryClient = useQueryClient()
  const importFileRef = useRef<HTMLInputElement>(null)

  const profilesQuery = useQuery({
    queryKey: ['terms', 'profiles'],
    queryFn: terminologyApi.listProfiles,
  })
  const profiles = profilesQuery.data ?? []

  const [addOpen, setAddOpen] = useState(false)
  const [editProfile, setEditProfile] = useState<TermProfile | null>(null)
  const [importTarget, setImportTarget] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [status, setStatus] = useState<Status>(null)

  const active = i18nInstance.resolvedLanguage

  async function refresh() {
    const fresh = await queryClient.fetchQuery({
      queryKey: ['terms', 'profiles'],
      queryFn: terminologyApi.listProfiles,
      // The list just changed: a copy cached within staleTime would be applied,
      // and persisted for the next load, with the deleted language still in it.
      staleTime: 0,
    })
    applyLocales(fresh)
  }

  const remove = useMutation({
    mutationFn: (id: string) => terminologyApi.deleteProfile(id),
    onMutate: (id) => setBusyId(id),
    onSuccess: async (_data, id) => {
      setStatus(null)
      if (active === id) await i18nInstance.changeLanguage('en-standard')
      await refresh()
    },
    onError: (e) =>
      setStatus({ type: 'err', messages: [e instanceof Error ? e.message : 'Delete failed'] }),
    onSettled: () => setBusyId(null),
  })

  async function handleExport(id: string) {
    setStatus(null)
    setBusyId(id)
    try {
      await exportLocaleCsv(id)
    } catch (e) {
      setStatus({ type: 'err', messages: [e instanceof Error ? e.message : 'Export failed'] })
    } finally {
      setBusyId(null)
    }
  }

  async function handleImportFile(file: File) {
    if (!importTarget) return
    const id = importTarget
    setBusyId(id)
    setStatus(null)
    try {
      const result = await importLocaleCsv(id, file)
      setStatus({ type: result.ok ? 'ok' : 'err', messages: result.messages })
      // Refresh on full OR partial success: per-namespace saves aren't atomic,
      // so a partial failure can still have persisted real changes.
      if (result.ok || result.partial) {
        await queryClient.invalidateQueries({ queryKey: ['terms'] })
      }
    } catch (e) {
      setStatus({ type: 'err', messages: [e instanceof Error ? e.message : 'Import failed'] })
    } finally {
      setBusyId(null)
      setImportTarget(null)
      if (importFileRef.current) importFileRef.current.value = ''
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Languages</CardTitle>
          <CardDescription>
            Terminology profiles. New languages inherit all terms until translated.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add language
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {status && (
          <Alert variant={status.type === 'err' ? 'destructive' : 'default'}>
            <AlertTitle>{status.type === 'err' ? 'Problem' : 'Done'}</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-4">
                {status.messages.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <ul className="divide-y rounded-md border">
          {profiles.map((p) => {
            const isBusy = busyId === p.id
            return (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{p.label}</span>
                    {p.isBase && <Badge variant="secondary">base</Badge>}
                    {active === p.id && <Badge>active</Badge>}
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">
                    {p.id}
                    {p.fallback ? ` → ${p.fallback}` : ''}
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditProfile(p)}
                    disabled={isBusy}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    Edit terms
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Export CSV"
                    aria-label={`Export CSV for ${p.label}`}
                    onClick={() => handleExport(p.id)}
                    disabled={isBusy}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Import CSV"
                    aria-label={`Import CSV for ${p.label}`}
                    onClick={() => {
                      setImportTarget(p.id)
                      importFileRef.current?.click()
                    }}
                    disabled={isBusy}
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={p.isBase ? 'The base locale cannot be deleted' : `Delete ${p.label}`}
                    aria-label={
                      p.isBase ? 'Base locale cannot be deleted' : `Delete ${p.label}`
                    }
                    disabled={p.isBase || isBusy}
                    onClick={() => {
                      if (window.confirm(`Delete language "${p.label}" (${p.id})?`)) {
                        remove.mutate(p.id)
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </CardContent>

      {/* Shared hidden file input for per-row CSV import. */}
      <input
        ref={importFileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleImportFile(file)
        }}
      />

      <AddLanguageModal open={addOpen} onOpenChange={setAddOpen} profiles={profiles} />
      <EditTermsModal
        open={editProfile !== null}
        onOpenChange={(o) => !o && setEditProfile(null)}
        profile={editProfile}
      />
    </Card>
  )
}
